<template>
  <div class="standard-page v-flex">
    <!-- BP-1546: the two-way toggle, styled and spaced like the Templates page's automation pills. -->
    <div class="groups-toggle" role="tablist" aria-label="Groups page section">
      <button
        v-for="section in sections"
        :key="section.value"
        type="button"
        role="tab"
        class="groups-pill"
        :class="{ 'groups-pill--active': activeSection === section.value }"
        :aria-selected="String(activeSection === section.value)"
        :data-cy="`groups-section-${section.value}`"
        @click="activeSection = section.value"
      >
        {{ section.label }}
      </button>
    </div>

    <b-button-toolbar
      v-if="activeSection === 'list'"
      class="button-toolbar groups-toolbar"
      aria-label="Groups toolbar"
    >
      <b-input-group id="search-group">
        <b-form-input
          id="search-input"
          v-model="search"
          placeholder="Search..."
          type="text"
          data-cy="groups-search"
        />
        <b-input-group-text class="search-icon">
          <Search />
        </b-input-group-text>
      </b-input-group>

      <b-button
        id="create-group-button"
        variant="blue"
        data-cy="create-group"
        @click="openEditor(null)"
      >
        Create Group
      </b-button>
    </b-button-toolbar>
    <GroupsList
      v-if="activeSection === 'list'"
      :search="search"
      :open-group-pkid="openGroupPkid"
      data-cy="groups-list-section"
      @edit="openEditor"
    />
    <GroupForm
      v-else
      :group="editingGroup"
      :template-id="presetTemplateId"
      data-cy="groups-edit-section"
      @cancel="activeSection = 'list'"
      @saved="activeSection = 'list'"
      @deleted="activeSection = 'list'"
    />
  </div>
</template>

<script lang="ts">
import { Search } from 'lucide-vue'
import { Component, Vue } from 'vue-property-decorator'
import GroupForm from './GroupForm.vue'
import GroupsList from './GroupsList.vue'

/** `?template=` and `?group=` are pkids. A UUID or any other non-integer is ignored. */
function queryPkid(value: unknown): number | null {
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) return null
  const pkid = Number(value)
  if (!Number.isSafeInteger(pkid) || String(pkid) !== value) return null

  return pkid
}

@Component({ components: { GroupForm, GroupsList, Search } })
export default class GroupsView extends Vue {
  sections = [
    { value: 'list', label: 'Groups list' },
    { value: 'edit', label: 'Create / edit group' }
  ]

  activeSection: 'list' | 'edit' = 'list'
  search = ''
  /** The group being edited; null means "create". Read by the editor once it exists. */
  editingGroup: object | null = null

  /** From a template's "+ Add Automated Text": `?section=edit&template=<pkid>` opens a blank editor. */
  presetTemplateId: number | null = null

  // From a group link on the Templates page: `?group=<pkid>`. The list opens it once loaded, then this clears.
  openGroupPkid: number | null = null

  mounted() {
    this.$store.dispatch('setPageTitle', [{ crumbTitle: 'Groups' }])
    const { section, template, group } = this.$route.query
    if (section === 'edit') {
      this.presetTemplateId = queryPkid(template)
      this.openEditor(null)
    } else {
      this.openGroupPkid = queryPkid(group)
    }
  }

  openEditor(group: object | null) {
    this.openGroupPkid = null
    this.editingGroup = group
    this.activeSection = 'edit'
  }
}
</script>

<style lang="scss" scoped>
// Same 25px left edge as .button-toolbar (scss/main.scss). The toggle owns the
// 24px of air below it, so the list toolbar and the edit form sit at the same
// distance from it.
.groups-toggle {
  display: flex;
  gap: 4px;
  margin: 25px 25px 24px;
}

.groups-toolbar {
  margin-top: 0;
}

.groups-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 14px;
  color: $darker-gray;

  font-size: 14px;
  font-weight: 600;
  line-height: 1;

  background: transparent;
  border: 1px solid transparentize($darker-gray, 0.55);
  border-radius: 999px;
  cursor: pointer;
  transition:
    color 0.15s,
    background-color 0.15s,
    border-color 0.15s;

  &:hover:not(.groups-pill--active) {
    color: $blue;

    border-color: $button-blue;
  }

  &:focus-visible {
    outline: 2px solid $bright-blue;
    outline-offset: 2px;
  }

  &--active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }
}

.search-icon svg {
  margin: 0 10px;
}

// Right-aligned like #create-template-button on the Templates page, same `blue` variant.
#create-group-button {
  margin: 0 5px 0 auto;
}
</style>
